/* eslint-disable react/only-export-components -- lib module: hooks, helpers and route guards live together on purpose */
// Auth state (ARCHITECTURE 4.1 and 15): useMe() on /api/auth/me. A 401 is the normal logged out state.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { api, isApiError, isMfaChallenge } from './api'
import { setViewerTimeZone } from './format'
import { syncLanguageFromUser } from './i18n'
import { shouldRetry } from './queryClient'
import { getServiceWorkerRegistration } from './pwa'
import { setRealtimeAuth } from './realtime'
import { syncThemeFromUser } from './theme'
import { analyticsUserId, identifyUser, markSignedOut, resetAnalytics } from './analytics'
import { useConsentStore } from './consentStore'

export type PlatformRole = 'user' | 'creator' | 'admin'
export type MemberRole = 'owner' | 'admin' | 'moderator' | 'member'
export type MemberStatus = 'pending' | 'active' | 'churned' | 'left' | 'removed' | 'banned' | 'declined'

export type UserBrief = { id: string; name: string; handle: string; avatarUrl: string | null }
export type MemberBrief = UserBrief & { level: number; role: MemberRole }

export type UserMe = UserBrief & {
  email: string
  emailVerified: boolean
  firstName: string
  lastName: string
  bio: string | null
  platformRole: PlatformRole
  theme: 'light' | 'dark' | 'system' | null
  language: string | null
  timezone: string | null
  dmPolicy: string
  chatEnabled: boolean
  profileComplete: boolean
  createdAt?: string | null
  /** The product analytics choice on the account (docs/analytics.md); null when the person never answered. */
  analyticsConsent?: { granted: boolean; source: string; version: string; at: string } | null
}

export type MeCommunity = {
  id: string
  slug: string
  name: string
  iconUrl: string | null
  brandColor: string | null
  role: MemberRole
  status: MemberStatus
  pinned: boolean
  hiddenInSwitcher: boolean
  switcherPosition: number | null
  level?: number
  points?: number
}

export type Me = {
  user: UserMe
  communities: MeCommunity[]
  unread: { notifications: number; chats: number }
}

export const ME_KEY = ['me'] as const

/**
 * GET /api/auth/me; resolves null when logged out. Accepts both the 401 and a 200 `{"user": null}`
 * answer, so the API can drop the 401 that shows up as a console error for every visitor (Q-COMM-12).
 */
export async function fetchMe(): Promise<Me | null> {
  try {
    const me = await api.get<Me | { user: null } | null>('/api/auth/me')
    if (!me || !me.user) return nobody()
    applyUserPreferences(me as Me)
    return me as Me
  } catch (e) {
    if (isApiError(e) && e.status === 401) return nobody()
    // N-03: the login still waits for the second factor and the code dialog was closed: that half done session goes
    if (isMfaChallenge(e) && e.fields.login === true) {
      await api.post('/api/auth/logout').catch(() => undefined)
      return nobody()
    }
    throw e
  }
}

/** Nobody is signed in. A session this tab knew ended without a sign out here (revoked on another device, expired,
 *  signed out in another tab, the account deleted): the same teardown as a sign out (endSession), except the person's
 *  drafts (E2E-J4-02, 2026-10-08): they are keyed by the person's id, so the next account never sees them, and they wait
 *  until the same person signs in again. Only a sign out here forgets them, whether or not a tab was open when the
 *  session ended. Analytics also drop a person's id kept from an earlier visit (code review 1). */
function nobody(): null {
  const known = analyticsUserId()
  if (known) endSession(known, { keepDrafts: true })
  else {
    setRealtimeAuth(false)
    markSignedOut()
  }
  return null
}

/**
 * Everything a sign out leaves behind in this browser, in one place (useLogout, revoking this device in Settings, a
 * session found ended, which keeps the drafts): the person's local drafts, the realtime connection, their analytics
 * identity (reset first, while Mixpanel may still run, so nothing of theirs survives a later yes in this tab) and an
 * analytics answer that was theirs (the browser's own answer comes back, docs/analytics.md).
 */
export function endSession(userId?: string | null, { keepDrafts = false }: { keepDrafts?: boolean } = {}): void {
  if (userId && !keepDrafts) forgetUserStorage(userId)
  setRealtimeAuth(false)
  resetAnalytics()
  useConsentStore.getState().signOut()
}

function applyUserPreferences(me: Me): void {
  setViewerTimeZone(me.user.timezone)
  syncLanguageFromUser(me.user.language)
  syncThemeFromUser(me.user.theme)
  setRealtimeAuth(true)
  identifyUser({ ...me.user, communities: me.communities })
}

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: (count, error) => count < 1 && shouldRetry(count, error),
    retryDelay: 1000,
  })
}

/** Convenience view over useMe(): a failed call (backend down) reads as logged out. */
export function useAuth() {
  const q = useMe()
  const me = q.data ?? null
  return {
    me,
    user: me?.user ?? null,
    isLoggedIn: !!me,
    isLoading: q.isPending,
    isVerified: !!me?.user.emailVerified,
    isPlatformAdmin: me?.user.platformRole === 'admin',
    refetch: q.refetch,
  }
}

/** "/login?next=<current path>" for links and redirects. */
export function loginPath(next?: string): string {
  return next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login'
}

/**
 * Only same site paths are allowed as ?next= targets (no open redirects). SEC2-HTTP-03: browsers drop tabs and
 * newlines and read "\" as "/" while parsing a URL, so "/\t/evil.example" opened in a new tab leaves the site:
 * control characters, spaces and backslashes are refused anywhere, and the rest must resolve to this origin.
 */
export function safeNext(next: string | null | undefined, fallback = '/'): string {
  // eslint-disable-next-line no-control-regex
  if (!next || !next.startsWith('/') || next.startsWith('//') || /[\u0000- \u007f\\]/.test(next)) return fallback
  try {
    const origin = window.location.origin
    const url = new URL(next, origin)
    return url.origin === origin ? url.pathname + url.search + url.hash : fallback
  } catch {
    return fallback
  }
}

/**
 * SEC2-PEOPLE-03: a signed out browser must stop showing DM and mention pushes. Unsubscribes this browser and
 * removes its row while the session still works (the API also drops the session's subscriptions on log out).
 * Best effort and bounded, so logging out never hangs on the service worker.
 */
export async function dropBrowserPush(timeoutMs = 2000): Promise<void> {
  const work = (async () => {
    const reg = await getServiceWorkerRegistration()
    const sub = await reg?.pushManager?.getSubscription()
    if (!sub) return
    const endpoint = sub.endpoint
    await sub.unsubscribe().catch(() => false)
    await api.del('/api/push/subscriptions', { endpoint }).catch(() => undefined)
  })().catch(() => undefined)
  await Promise.race([work, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))])
}

/**
 * E2E-J4-02: keys that hold one person's data on this device follow "mgs-<name>:<userId>:..." (feed drafts).
 * Logging out removes that person's keys, so the next person on a shared computer cannot read them.
 */
export function forgetUserStorage(userId: string, storage: Pick<Storage, 'length' | 'key' | 'removeItem'> = localStorage): void {
  try {
    const mine: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i)
      if (k && k.startsWith('mgs-') && k.includes(`:${userId}:`)) mine.push(k)
    }
    mine.forEach((k) => storage.removeItem(k))
  } catch {
    /* storage blocked: nothing was kept either */
  }
}

export function useLogout() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async () => {
      await dropBrowserPush()
      await api.post<void>('/api/auth/logout')
    },
    onSettled: () => {
      endSession(qc.getQueryData<Me | null>(ME_KEY)?.user.id)
      qc.setQueryData(ME_KEY, null)
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== ME_KEY[0] })
      navigate('/', { replace: true })
    },
  })
}

/** Neutral full page placeholder while /api/auth/me is in flight. */
function AuthPending() {
  return <div className="min-h-[50vh]" aria-busy="true" />
}

/** Route guard: logged out visitors go to /login?next=<path>. Wrap children or use as a layout route. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { isLoggedIn, isLoading } = useAuth()
  const location = useLocation()
  if (isLoading) return <AuthPending />
  if (!isLoggedIn) return <Navigate to={loginPath(location.pathname + location.search)} replace />
  return <>{children ?? <Outlet />}</>
}

/** Route guard: unverified users go to /verify-email?next=<path>. Implies RequireAuth. */
export function RequireVerified({ children }: { children?: ReactNode }) {
  const { isLoggedIn, isLoading, isVerified } = useAuth()
  const location = useLocation()
  const here = location.pathname + location.search
  if (isLoading) return <AuthPending />
  if (!isLoggedIn) return <Navigate to={loginPath(here)} replace />
  if (!isVerified) return <Navigate to={`/verify-email?next=${encodeURIComponent(here)}`} replace />
  return <>{children ?? <Outlet />}</>
}

/**
 * Route guard for the super admin console (platform_role admin). Everyone else, logged out visitors included,
 * sees the fallback (the 404 page): no login redirect, so nobody learns that a private admin area exists
 * (REDESIGN 10, Q-ADMIN-01). MG loses nothing: any login without a next already lands him on /admin.
 */
export function RequirePlatformAdmin({ children, fallback }: { children?: ReactNode; fallback: ReactNode }) {
  const { isLoggedIn, isLoading, isPlatformAdmin } = useAuth()
  if (isLoading) return <AuthPending />
  if (!isLoggedIn || !isPlatformAdmin) return <>{fallback}</>
  return <>{children ?? <Outlet />}</>
}

const ROLE_RANK: Record<MemberRole, number> = { member: 0, moderator: 1, admin: 2, owner: 3 }

export function roleAtLeast(role: MemberRole | null | undefined, min: MemberRole): boolean {
  return !!role && ROLE_RANK[role] >= ROLE_RANK[min]
}

/** The viewer's membership in a community, read from the me payload (no extra request). */
export function useMembership(slug: string | undefined) {
  const { me } = useAuth()
  const s = slug?.toLowerCase()
  const membership = (s && me?.communities.find((c) => c.slug.toLowerCase() === s)) || null
  const active = membership?.status === 'active'
  return {
    membership,
    role: membership?.role ?? null,
    status: membership?.status ?? null,
    isMember: active,
    isPending: membership?.status === 'pending',
    isBanned: membership?.status === 'banned',
    isOwner: active && membership?.role === 'owner',
    isAdmin: active && roleAtLeast(membership?.role, 'admin'),
    isModerator: active && roleAtLeast(membership?.role, 'moderator'),
  }
}

/**
 * Saves theme or language on the account (best effort; the local choice already applied).
 * Endpoint: PATCH /api/me/preferences (S1 accounts). Failures are ignored on purpose.
 */
export async function saveUserPreference(
  qc: ReturnType<typeof useQueryClient>,
  patch: Partial<Pick<UserMe, 'theme' | 'language' | 'timezone'>>,
): Promise<void> {
  qc.setQueryData<Me | null>(ME_KEY, (old) => (old ? { ...old, user: { ...old.user, ...patch } } : old))
  try {
    await api.patch('/api/me/preferences', patch)
  } catch {
    /* the preference still applies on this device */
  }
}
